<script setup>
import notification from "@/utils/plugins/NotificationService";
import { Select, CloseBold } from '@element-plus/icons-vue';
import { computed, nextTick, ref } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";

import moment from 'moment';

// Services
import EmployeeNotificationService from '@/api/services/employeeNotification.service';
import DepartmentNotificationService from '@/api/services/departmentNotification.service';
import VehicleRequestService from "@/api/services/vehicleRequest.service";

// My components
import VehicleRejectDialog from "@/components/shared/Vehicles/VehicleRejectDialog.vue";

const title = computed(() => {
    if (props && props.title) {
        return props.title;
    }
    return '';
})

const selectedMail = computed(() => {
    if (props && props.mail) {
        return props.mail;
    }
    return null;
})

const emailRead = computed(() => {
    if (selectedMail.value && selectedMail.value.emailRead) {
        return selectedMail.value.emailRead;
    }
    return false;
})

const actionMetadata = computed(() => selectedMail.value?.actionMetadata ?? null)

const vehicleRequestActions = computed(() => actionMetadata.value?.actions ?? [])

const showVehicleRequestActions = computed(() => {
    return actionMetadata.value?.entityType === 'VehicleRequest'
        && vehicleRequestActions.value.length > 0
        && !selectedMail.value?.actionTaken
})

const canApproveVehicleRequest = computed(() => {
    return showVehicleRequestActions.value && vehicleRequestActions.value.includes('approve')
})

const canRejectVehicleRequest = computed(() => {
    return showVehicleRequestActions.value && vehicleRequestActions.value.includes('reject')
})

const selectedRequest = ref(null);
const dialogRejectRequest = ref(false);
const uiStore = useUiStore();

// eslint-disable-next-line no-undef
const props = defineProps({
    title: String,
    mail: Object,
})
// eslint-disable-next-line no-undef
const emits = defineEmits(['closeDialog']);

const closeDialog = (read = false) => {
    emits('closeDialog', read);
}

const approveVehicleRequest = async () => {
    const entityId = actionMetadata.value?.entityId;

    if (!entityId) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido la solicitud de vehículo',
            duration: 6000
        })
        return;
    }

    try {
        uiStore.startLoading();
        const res = await VehicleRequestService.approveVehicleRequest(entityId);
        if (res && (res.status === 200 || res.status === 201)) {
            notification.addNotification({
                type: 'success',
                message: 'Solicitud aprobada',
                duration: 4000
            })
            closeDialog(true);
        }
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}

const openDialogRejectVehicleRequest = async () => {
    const entityId = actionMetadata.value?.entityId;

    if (!entityId) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido la solicitud de vehículo',
            duration: 6000
        })
        return;
    }

    try {
        uiStore.startLoading();
        const res = await VehicleRequestService.getVehicleRequest(entityId);
        if (res && res.status === 200) {
            selectedRequest.value = res.data;
            dialogRejectRequest.value = true;
        }
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'Error al cargar los vehículos.',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}

const closeDialogRejectVehicleRequest = () => {
    dialogRejectRequest.value = false;
    selectedRequest.value = null;
}

const finishRejectVehicleRequest = async () => {
    closeDialogRejectVehicleRequest();
    await nextTick();
    closeDialog(true);
}

const markRead = () => {
    //Marcar leido o no leido
    if (!selectedMail.value || !selectedMail.value.id) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido la notificación correctamente',
            duration: 6000
        })
        return;
    }
    markAsReadItem()
        .then((res) => {
            if (res && res.status && res.status === 200) {
                closeDialog(true);
            } else {
                closeDialog();
            }
        })
        .catch(error => {
            console.error("Error marcando como leído:", error)
            notification.addNotification({
                title: "Error",
                message: "No se pudo marcar como leído",
                type: "error",
            })
            closeDialog();
        })
}

const markAsReadItem = async () => {
    if (!selectedMail.value || !selectedMail.value['@type']) {
        throw new Error("Tipo de notificación no definido")
    }

    const params = { emailRead: !selectedMail.value.emailRead }

    if (selectedMail.value['@type'] === "DepartmentNotification") {
        return DepartmentNotificationService.updateDepartmentNotification(
            selectedMail.value.id,
            params
        )
    } else if (selectedMail.value['@type'] === "EmployeeNotification") {
        return EmployeeNotificationService.updateEmployeeNotification(
            selectedMail.value.id,
            params
        )
    }

    throw new Error(`Tipo no soportado: ${selectedMail.value['@type']}`)
}


const handleBodyClick = async (event) => {
    const anchor = event.target.closest('a[href]');
    if (!anchor) return;

    const match = anchor.href.match(/\/api\/vehicle_requests\/(\d+)\/confirm/);
    if (!match) return;

    event.preventDefault();

    if (selectedMail.value?.actionTaken) return;
    const id = parseInt(match[1]);

    try {
        const params = selectedMail.value?.id ? { notificationId: selectedMail.value.id } : {};
        const res = await VehicleRequestService.confirmVehicleRequest(id, params);
        if (res?.status === 200 || res?.status === 201) {
            notification.addNotification({ type: 'success', message: 'Se confirmó correctamente la devolución del vehículo', duration: 6000 });
            emits('reloadInbox');
            closeDialog(true);
        }
    } catch {
        notification.addNotification({ type: 'error', message: 'Ha ocurrido un error al confirmar la devolución', duration: 6000 });
    }
}

const formatDate = (dateString) => {
    moment.locale('es');
    const m = moment(dateString);
    if (!m.isValid()) {
        return '';
    }
    const now = moment();
    const startOfToday = now.clone().startOf('day');
    const startOfYesterday = startOfToday.clone().subtract(1, 'day');
    const timePart = m.format('HH:mm');
    if (m.isSame(startOfToday, 'day')) {
        return `Hoy ${timePart}`;
    }
    if (m.isSame(startOfYesterday, 'day')) {
        return `Ayer ${timePart}`;
    }
    if (m.isSame(now, 'week')) {
        const dayName = m.format('dddd');
        const dayNameCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1);
        return `${dayNameCapitalized} ${timePart}`;
    }
    return m.format('DD/MM/YYYY HH:mm');
}
</script>

<template>
    <div>
        <div class="my-header">
            <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="724" height="1024" viewBox="0 0 724 1024"
                v-if="title !== ''">
                <path
                    d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
            </svg>
            <span class="text-header ml-5 inbox-title"> {{ title }}</span>
            <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
                @click="closeDialog()">
        </div>
        <div>
        </div>
        <div class="inbox-body" v-if="selectedMail">
            <div class="subject-mail mb-1">
                <span class="subject-text">{{ selectedMail.subject }}</span>
            </div>
            <div class="from-mail mb-1" v-if="selectedMail.sender">
                <span class="blue-text">De: </span>
                <span>{{ selectedMail.sender }}</span>
            </div>
            <div class="to-mail mb-1" v-if="selectedMail.to">
                <span class="blue-text">Para: </span>
                <span>{{ selectedMail.to }}</span>
            </div>
            <div class="to-mail mb-1" v-if="selectedMail.sentAt">
                <span class="blue-text">Enviado el: </span>
                <span>{{ formatDate(selectedMail.sentAt) }}</span>
            </div>

            <div class="body-mail mt-2" :class="{ 'body-mail--action-taken': selectedMail.actionTaken }"
                @click="handleBodyClick">
                <div v-html="selectedMail.content" class="custom-quill"></div>
            </div>
        </div>
        <div class="inbox-footer">
            <div class="vehicle-request-action-panel" v-if="showVehicleRequestActions">
                <div class="vehicle-request-action-panel__title">
                    Gestionar solicitud de vehículo
                </div>
                <div class="vehicle-request-action-panel__buttons">
                    <el-button class="btn btn_red vehicle-request-decision-button" :icon="CloseBold"
                        v-if="canRejectVehicleRequest" @click="openDialogRejectVehicleRequest">
                        Rechazar solicitud
                    </el-button>

                    <el-button class="btn btn_darkblue vehicle-request-decision-button" :icon="Select"
                        v-if="canApproveVehicleRequest" style="color: #fff;" @click="approveVehicleRequest">
                        Aprobar solicitud
                    </el-button>
                </div>
            </div>
            <div class="row mt-4">
                <div class="col d-flex justify-content-end gap-3 btn-responsive-stack">
                    <el-button class="btn btn_red px-4" @click="closeDialog()">
                        Cancelar
                    </el-button>

                    <el-button class="btn btn_yellow px-4" @click="markRead()" style="margin-left: 0px;">
                        {{ emailRead ? 'No leído' : 'Leído' }}
                    </el-button>
                </div>
            </div>
        </div>
        <el-dialog :model-value="dialogRejectRequest" :center="true" :lock-scroll="true" :show-close="false"
            :width="750" @close="closeDialogRejectVehicleRequest()" style="border-radius: 20px;">
            <VehicleRejectDialog @close="closeDialogRejectVehicleRequest()" @finish="finishRejectVehicleRequest()"
                :vehicleRequest="selectedRequest" v-if="dialogRejectRequest" />
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
.inbox-title {
    margin-left: 40px;
}

.inbox-body {
    .subject-text {
        font-size: 24px;
        font-weight: bold;
        color: $bg-blue;
    }

    .blue-text {
        color: $bg-blue;
        font-size: 17px;
        font-weight: 700;

    }

    .body-mail {
        font-size: 15px;

        .custom-quill {
            background-color: #f3f0fb;
            min-height: 150px;
            font-size: 15px;
        }
    }

    .body-mail--action-taken :deep(a) {
        pointer-events: none;
        opacity: 0.5;
        filter: grayscale(100%);
        cursor: not-allowed;
    }
}

.vehicle-request-action-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    margin-top: 24px;
    border: 1px solid rgba(71, 76, 230, 0.18);
    border-radius: 12px;
    background: #f8f7ff;
}

.vehicle-request-action-panel__title {
    color: $bg-blue;
    font-size: 16px;
    font-weight: 700;
}

.vehicle-request-action-panel__buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.vehicle-request-decision-button {
    min-width: 170px;
    min-height: 38px;
    margin-left: 0 !important;
    font-weight: 700;
}

.vehicle-request-action-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    margin-top: 24px;
    border: 1px solid rgba(71, 76, 230, 0.18);
    border-radius: 12px;
    background: #f8f7ff;
}

.vehicle-request-action-panel__title {
    color: $bg-blue;
    font-size: 16px;
    font-weight: 700;
}

.vehicle-request-action-panel__buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.vehicle-request-decision-button {
    min-width: 170px;
    min-height: 38px;
    margin-left: 0 !important;
    font-weight: 700;
}

@media (max-width: 720px) {
    .inbox-body {
        font-size: 19px;
    }

    .inbox-title {
        font-size: 2em !important;
    }

    #asterisk {
        width: 1.9em !important;
    }

    .vehicle-request-action-panel {
        align-items: stretch;
        flex-direction: column;
    }

    .vehicle-request-action-panel__buttons {
        flex-direction: column;
        align-items: stretch;
    }

    .vehicle-request-decision-button {
        width: 100%;
    }
}

@media (max-width: 430px) {
    .btn-responsive-stack {
        flex-direction: column !important;
        align-items: center;
    }

    .btn-responsive-stack>* {
        width: 95% !important;
        margin-bottom: 8px;
    }
}
</style>
